import { useState, useMemo, useCallback } from 'react';
import {
  Button,
  Checkbox,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@monksflow/monks-ui';
import { useUserWorkspaces } from '@/entities/user';
import { FormSidePanel } from '@/shared/ui/layout/FormSidePanel';
import type { AddWhitelistRuleModalProps } from './AddWhitelistRuleModal.types';
import { useTranslation } from 'react-i18next';

/**
 * The workspace picker is the design system's own `Select` rather than a ported one: a picker
 * that portals its menu to the body lands outside the sheet's dismissable layer, which leaves it
 * inert, and pinning the menu in place instead marks the whole sheet `aria-hidden`.
 */
export const AddWhitelistRuleModal = ({
  open,
  onClose,
  whitelistedProviders,
  onSave,
  isSaving,
}: AddWhitelistRuleModalProps) => {
  const { t } = useTranslation('admin');

  const workspaces = useUserWorkspaces();
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState('');
  const [selectedProviders, setSelectedProviders] = useState<string[]>([]);

  // Derive allowed providers (org-level only)
  const allowedProviders = useMemo(
    () => whitelistedProviders.filter((p) => !p.workspaceId),
    [whitelistedProviders],
  );

  const handleWorkspaceChange = (workspaceId: string) => {
    setSelectedWorkspaceId(workspaceId);
    // Seed the checkboxes from the rule the workspace already has, but only while the user
    // has not made a selection of their own to overwrite. Seeding from an effect keyed on
    // `selectedProviders` instead re-entered itself, because a workspace with no existing
    // rule stores a fresh empty array on every pass, and a tick racing that loop was lost.
    if (selectedProviders.length === 0) {
      setSelectedProviders(
        whitelistedProviders.filter((p) => p.workspaceId === workspaceId).map((p) => p.key),
      );
    }
  };

  const handleToggleProvider = (providerKey: string) => {
    setSelectedProviders((prev) =>
      prev.includes(providerKey) ? prev.filter((p) => p !== providerKey) : [...prev, providerKey],
    );
  };

  const resetForm = useCallback(() => {
    setSelectedWorkspaceId('');
    setSelectedProviders([]);
  }, []);

  const handleSave = useCallback(async () => {
    if (!selectedWorkspaceId || selectedProviders.length === 0) return;
    await onSave(selectedProviders, selectedWorkspaceId);
    resetForm();
  }, [selectedWorkspaceId, selectedProviders, onSave, resetForm]);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const canSave = Boolean(selectedWorkspaceId) && selectedProviders.length > 0;

  return (
    <FormSidePanel
      open={open}
      onOpenChange={(next) => !next && handleClose()}
      title={t('admin.addWhitelistRuleModal.addWhitelistRule')}
      className="w-[560px] gap-sm"
      footer={
        <>
          <Button variant="outline" size="lg" onClick={handleClose} disabled={isSaving}>
            {t('admin.addWhitelistRuleModal.cancel')}
          </Button>
          <Button
            variant="default"
            size="lg"
            onClick={() => void handleSave()}
            disabled={!canSave || isSaving}
          >
            {isSaving
              ? t('admin.addWhitelistRuleModal.adding')
              : t('admin.addWhitelistRuleModal.addRule')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-xl">
        <div className="flex flex-col gap-sm">
          <Label htmlFor="workspace-select">{t('admin.addWhitelistRuleModal.workspace')}</Label>
          <p className="text-sm text-muted-foreground">
            {t('admin.addWhitelistRuleModal.selectTheWorkspaceWhichThe')}
          </p>
          <Select
            value={selectedWorkspaceId}
            onValueChange={handleWorkspaceChange}
            disabled={isSaving}
          >
            <SelectTrigger id="workspace-select" className="w-full">
              <SelectValue placeholder={t('admin.addWhitelistRuleModal.selectAWorkspace')} />
            </SelectTrigger>
            <SelectContent>
              {workspaces.map((workspace) => (
                <SelectItem key={workspace.id} value={workspace.id}>
                  {workspace.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-sm">
          <Label>{t('admin.addWhitelistRuleModal.modelProviders')}</Label>
          <p className="text-sm text-muted-foreground">
            {t('admin.addWhitelistRuleModal.selectTheProviderThisModel')}
          </p>
          <div className="flex flex-col gap-xs">
            {allowedProviders.map((provider) => (
              <div key={provider.key} className="flex items-center gap-2">
                <Checkbox
                  id={`whitelist-provider-${provider.key}`}
                  checked={selectedProviders.includes(provider.key)}
                  onCheckedChange={() => handleToggleProvider(provider.key)}
                  disabled={isSaving}
                />
                <Label
                  htmlFor={`whitelist-provider-${provider.key}`}
                  className="text-base leading-6"
                >
                  {provider.name}
                </Label>
              </div>
            ))}
          </div>
        </div>
      </div>
    </FormSidePanel>
  );
};

AddWhitelistRuleModal.displayName = 'AddWhitelistRuleModal';
